import React, { useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Text, Box } from '@cad-arc';
import {
    ConsolidatedLocationView,
    EventPanelFormType,
    EventPanelMetadata,
    EventPanelOptions,
} from '@mark43/cad-api';
import { useBootstrapState } from '../../../../bootstrap/bootstrap-provider';
import { NoMembersIndicator } from '../../../tabs/components/no-members-indicator';
import { EntityPanelFormGroup } from '../../../../panels/entity-panels/entity-panel-components';
import { TabCard } from '../../../tabs/components/tab-card';
import { ConditionalWrapper } from '../../../../common/components/conditional-wrapper';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../../../../action-broadcaster/use-integrated-action-broadcaster';
import { getDefaultVehicle } from './event-panel-vehicle-helpers';
import { EventPanelFormVehicleFieldset } from './event-panel-form-vehicles-fieldset';
import { TowVehicleFields } from './event-panel-form-tow-vehicle-fields';
import { TowVehicleBaseKey, EventPanelVehicleBaseKey } from '../types';
import { useIsFieldEditable } from '../../use-is-field-editable';
import { FormInputButton } from '../../../../forms/integrated-form-components/form-button';
import { useFormHasHadDataSetAtLeastOnceContext } from '../../../event-state/form-has-had-data-set-at-least-once';
import { useTrackedFieldArray } from '../../../../forms/use-tracked-field-array';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { useEventMetadataStore } from '../../event-metadata';

export type EventPanelFormVehiclesFieldsProps = {
    form: UseFormReturn<EventPanelFormType>;
    eventPanelOptions: EventPanelOptions;
    showTitle?: boolean;
    asTowVehicle?: boolean;
    baseKey: EventPanelVehicleBaseKey;
    noMembersText?: string;
    displayAsCards?: boolean;
    // This field isn't used in the new involved persons / vehicles work.
    // It is being replaced by displayAsCards.
    // It will be removed along with the legacy fields.
    hideDexFields?: boolean;
    isReadOnly?: boolean;
    userManuallyAddedVehicleField?: boolean;
} & (
    | {
          allowMembersControl: true;
          addButtonText: string;
      }
    | {
          // If we don't allow members control,
          // we won't show the add button,
          // and thus don't need to provide text
          allowMembersControl?: false;
      }
);

export function EventPanelFormVehiclesFields(
    props: EventPanelFormVehiclesFieldsProps
): React.ReactElement {
    const [userManuallyAddedField, setUserManuallyAddedField] = useState(false);
    const { isFirstResponder } = useApplicationType();
    const eventForm = props.form;
    const eventPanelConfiguration = useBootstrapState().eventPanelConfiguration;
    const { fields, append, remove } = useTrackedFieldArray({
        control: eventForm.control,
        name: props.baseKey,
        keyName: 'rhfKey',
    });
    const eventId = eventForm.getValues().eventId;
    const { agencyEventNumber } =
        useEventMetadataStore()?.state?.metadata ?? ({} as EventPanelMetadata);
    const isFieldEditable = useIsFieldEditable();
    const location = useWatch({
        control: eventForm.control,
        name: 'location',
    }) as ConsolidatedLocationView | null;

    const handleActions: ActionHandler<'EVENT_V2'> = React.useCallback(
        (action) => {
            switch (action.type) {
                case 'ADD_TOW_VEHICLE_TO_EVENT_FORM': {
                    if (props.baseKey !== 'towVehicles') {
                        return;
                    }
                    if (action.payload.eventId === eventId && location) {
                        setUserManuallyAddedField(true);

                        append({
                            ...getDefaultVehicle({
                                eventPanelConfiguration,
                                towedFromLocation: location,
                            }),
                            ...action.payload.initialState,
                        });
                        return true;
                    }
                    break;
                }
            }
        },
        [props.baseKey, eventId, append, eventPanelConfiguration, location]
    );

    const formHasHadDataSetAtLeastOnce = useFormHasHadDataSetAtLeastOnceContext();
    useIntegratedActionBroadcaster({
        id: 'TOW_VEHICLE_PREFILL',
        windowType: 'EVENT_V2',
        handler: formHasHadDataSetAtLeastOnce ? handleActions : undefined,
        ignoreActiveWorkspaceWindowType: isFirstResponder,
    });

    const addVehiclesButton =
        props.allowMembersControl && isFieldEditable ? (
            <FormInputButton
                size="md"
                variant="ghost"
                leadingVisual="Add"
                data-testid={`add-${props.baseKey === 'towVehicles' ? 'tow-' : ''}vehicle-button`}
                onClick={() => {
                    append(
                        getDefaultVehicle({
                            eventPanelConfiguration,
                            towedFromLocation:
                                props.baseKey === 'towVehicles'
                                    ? (eventForm.getValues()
                                          .location as ConsolidatedLocationView | null) ?? undefined
                                    : undefined,
                        })
                    );
                    setUserManuallyAddedField(true);
                }}
            >
                {props.addButtonText}
            </FormInputButton>
        ) : undefined;

    return (
        <EntityPanelFormGroup data-testid="vehicle-form-section">
            {props.showTitle && fields.length > 0 ? (
                <Text variant="headingXs">Vehicles</Text>
            ) : undefined}
            {fields.length === 0 && props.noMembersText ? (
                <NoMembersIndicator
                    text={props.noMembersText}
                    icon="Vehicle"
                    action={addVehiclesButton}
                    value={fields}
                />
            ) : (
                <>
                    {fields.map((field, index) => {
                        return props.asTowVehicle ? (
                            <ConditionalWrapper
                                key={field.rhfKey}
                                condition={props.displayAsCards}
                                wrapper={(children) => <TabCard>{children}</TabCard>}
                            >
                                <TowVehicleFields
                                    form={eventForm}
                                    field={field}
                                    index={index}
                                    remove={remove}
                                    baseKey={props.baseKey as TowVehicleBaseKey}
                                    eventPanelOptions={props.eventPanelOptions}
                                    allowMembersControl={props.allowMembersControl}
                                    eventId={eventId}
                                    eventNumber={agencyEventNumber}
                                    isReadOnly={props.isReadOnly}
                                    userManuallyAddedField={
                                        props.userManuallyAddedVehicleField ||
                                        userManuallyAddedField
                                    }
                                />
                            </ConditionalWrapper>
                        ) : (
                            <ConditionalWrapper
                                key={field.rhfKey}
                                condition={props.displayAsCards}
                                wrapper={(children) => <TabCard>{children}</TabCard>}
                            >
                                <EventPanelFormVehicleFieldset
                                    form={eventForm}
                                    field={field}
                                    index={index}
                                    remove={remove}
                                    baseKey={props.baseKey as 'vehicles' | 'mergedVehicles'}
                                    eventPanelOptions={props.eventPanelOptions}
                                    allowMembersControl={props.allowMembersControl}
                                    eventId={eventId}
                                    displayAsCards={props.displayAsCards}
                                    isReadOnly={props.isReadOnly}
                                    userManuallyAddedField={
                                        props.userManuallyAddedVehicleField ||
                                        userManuallyAddedField
                                    }
                                />
                            </ConditionalWrapper>
                        );
                    })}

                    <Box>{addVehiclesButton}</Box>
                </>
            )}
        </EntityPanelFormGroup>
    );
}
